import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDownload } from "react-icons/md";
import { MdUploadFile } from "react-icons/md";
import Select from "react-select";

import Button from "../../../components/Button";
import LoadingBtn from "../../../components/LoadingBtn";
import ImportClassroomsModal from "../../../components/modals/school/ImportClassroomsModal";
import { useTheme } from "../../../context/ThemeContext";
import useAuth from "../../../hooks/useAuth";
import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import { selectStylesV2 } from "../../../styles/selectStyles";

// Importing download icon from React Icons

const SchoolImportStudents = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { showSnack } = useSnack();
  const importModalRef = useRef();
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [_, setUploadedFile] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImport = () => {
    console.log("Imported successfully");
  };

  const handleSelect = (option) => {
    setSelectedTeacher(option);
  };

  const handleUpload = async (e) => {
    setIsSubmitting(true);
    const fileInput = e.target;
    const file = fileInput.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const teacherId = selectedTeacher?.value;
      if (!teacherId) return;

      const response = await classroomServices.previewClassroomsSheet(teacherId, formData);
      setIsSubmitting(false);
      importModalRef.current.openModal({
        file,
        teacherId,
        teacherName: selectedTeacher.label,
        uploadedData: response,
      });

      fileInput.value = "";
    } catch (error) {
      setIsSubmitting(false);
      showSnack(error, "error");
    }
  };

  const exportClassroomTemplate = async () => {
    try {
      const response = await classroomServices.exportClassroomTemplate();
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "classroom_template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting classroom template:", err);
    }
  };

  const getTeachers = async () => {
    setTeachersLoading(true);
    try {
      const response = await schoolServices.getTeachersBySchool(user?.school?.id);
      const formattedTeachers = response.data
        .filter((item) => !item.is_disabled)
        .map((teacher) => ({
          label: teacher.name,
          value: teacher.id,
        }));

      setTeachers(formattedTeachers);
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setTeachersLoading(false);
    }
  };

  useEffect(() => {
    getTeachers();
  }, []);

  return (
    <div className="space-y-4">
      {/* Select Teacher Card */}
      <div className="rounded-xl bg-white p-6 shadow-md dark:bg-dark-surface">
        <h3 className="text-lg font-semibold text-blue-500">1. {t("select_teacher_to_upload")}</h3>
        <Select
          isLoading={teachersLoading}
          noOptionsMessage={() => <p>{t("no_options")}</p>}
          loadingMessage={() => <p>{t("loading")}</p>}
          isClearable
          options={teachers}
          value={selectedTeacher}
          onChange={handleSelect}
          placeholder={t("select_teacher")}
          styles={selectStylesV2(theme)}
          className="mt-2"
        />
      </div>

      {/* Download Template Card */}
      <div className="flex items-center justify-between rounded-xl bg-white p-6 shadow-md dark:bg-dark-surface">
        <h3 className="text-lg font-semibold text-blue-500">{t("downloadSheet")}</h3>
        <Button
          color="green"
          variant="outlined"
          label={t("download") + " " + "excel sheet"}
          iconLeft={<MdDownload size={22} />}
          disabled={!selectedTeacher}
          onClick={exportClassroomTemplate}
        />
      </div>

      {/* Upload File Card */}
      <div className="rounded-xl bg-white p-6 shadow-md dark:bg-dark-surface">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-blue-500">{t("uploadSheet")}</h3>
          <label
            htmlFor="fileUpload"
            className={`flex items-center justify-center space-x-2 rounded-md px-4 ${isSubmitting ? "py-[5px]" : "py-2"} text-center text-white transition duration-300 hover:scale-[1.02] ${
              isSubmitting ? "py-[5px]" : "py-2"
            } ${
              selectedTeacher
                ? isSubmitting
                  ? "cursor-not-allowed bg-green-500"
                  : "cursor-pointer bg-green-500 hover:bg-green-600"
                : "cursor-not-allowed bg-gray-100"
            }`}
            style={!selectedTeacher ? { pointerEvents: "none" } : {}}
          >
            {isSubmitting ? (
              <LoadingBtn />
            ) : (
              <span className="flex items-center gap-2">
                <MdUploadFile size={22} />
                <span className="mx-4 text-sm font-medium">{t("upload") + " " + "excel sheet"}</span>
              </span>
            )}
          </label>
        </div>
        {!isSubmitting && (
          <input
            id="fileUpload"
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => {
              handleUpload(e);
              setUploadedFile(e.target.files[0]?.name || null);
            }}
            className="hidden"
          />
        )}
      </div>

      <ImportClassroomsModal ref={importModalRef} onSubmit={handleImport} />
    </div>
  );
};

export default SchoolImportStudents;
